/**
 * Hypehub CRO V2 — small progressive enhancements on top of Horizon.
 * 1. Explicit size selection on multi-size products (no silent default size).
 * 2. "Birlikte iyi gider" add buttons inside the cart drawer / cart page.
 * 3. Conditional "Haberdar ol" sign-up dialog.
 * Nothing here changes prices, totals or analytics events; cart math stays with Shopify.
 */

const HH = window.HH_CONFIG || {};
const SIZE_TEXT = 'Beden seç';
const SIZE_HINT = 'Devam etmek için bedenini seç.';

/* ------------------------------------------------------------------ */
/* 1. Explicit size selection                                          */
/* ------------------------------------------------------------------ */

/** Product+context keys where the shopper has already picked a size. */
const chosen = new Set();

/** @param {HTMLElement} picker */
function pickerKey(picker) {
  return `${picker.dataset.productId}:${picker.closest('dialog') ? 'dialog' : 'page'}`;
}

/** @param {HTMLElement} picker */
function scopeOf(picker) {
  return picker.closest('dialog') || picker.closest('.shopify-section') || document;
}

/** @param {HTMLElement} picker */
function buttonsFor(picker) {
  const scope = scopeOf(picker);
  const id = picker.dataset.productId;
  const form = scope.querySelector(`product-form-component[data-product-id="${id}"]`);
  const main = form?.querySelector('[ref="addToCartButton"]');
  const sticky = picker.closest('dialog')
    ? null
    : document.querySelector(`sticky-add-to-cart[data-product-id="${id}"] [ref="addToCartButton"]`) ||
      document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
  const express = form?.querySelector('.shopify-payment-button, shopify-accelerated-checkout, .accelerated-checkout-block');
  return { form, main, sticky, express };
}

/** Returns the text node that carries the main button label. */
function labelNode(button) {
  if (!button) return null;
  const stickyLabel = button.querySelector('.add-to-cart-text__content > span');
  if (stickyLabel) return stickyLabel;
  return Array.from(button.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()) || null;
}

function setLabel(button, text) {
  const node = labelNode(button);
  if (!node) return;
  if (button.dataset.hhOriginalLabel === undefined) button.dataset.hhOriginalLabel = node.textContent;
  node.textContent = node.nodeType === Node.TEXT_NODE ? `\n${text}\n` : text;
}

function restoreLabel(button) {
  if (!button || button.dataset.hhOriginalLabel === undefined) return;
  const node = labelNode(button);
  if (node) node.textContent = button.dataset.hhOriginalLabel;
  delete button.dataset.hhOriginalLabel;
}

/** @param {HTMLElement} picker */
function hintFor(picker) {
  let hint = picker.querySelector('.hh-size-hint');
  if (!hint) {
    hint = document.createElement('p');
    hint.className = 'hh-size-hint';
    hint.setAttribute('role', 'status');
    hint.setAttribute('aria-live', 'polite');
    picker.querySelector('fieldset')?.after(hint);
  }
  return hint;
}

/** @param {HTMLElement} picker */
function requireSelection(picker) {
  if (picker.dataset.hhNeedsSelection === 'true') return;
  const key = pickerKey(picker);
  if (chosen.has(key)) return;

  const isMainPage = picker.dataset.templateProductMatch === 'true' && !picker.closest('dialog');
  if (isMainPage && new URL(window.location.href).searchParams.has('variant')) {
    chosen.add(key); // deep link or cart edit: keep the linked size
    return;
  }

  const radios = Array.from(picker.querySelectorAll('fieldset input[type="radio"]'));
  if (radios.length < 2) return;
  if (!radios.some((r) => r.dataset.optionAvailable === 'true')) return; // fully sold out: let Horizon show it

  const { main, sticky, express } = buttonsFor(picker);
  if (!main || main.disabled) return;

  radios.forEach((r) => {
    r.checked = false;
    // Removing the attribute as well makes browsers re-evaluate :has(:checked) styles
    // (setting only the property left the first label painted as "selected").
    r.removeAttribute('checked');
    r.dataset.currentChecked = 'false';
    r.dataset.previousChecked = 'false';
  });
  picker.dataset.hhNeedsSelection = 'true';
  picker.querySelectorAll('fieldset').forEach((fs) => {
    fs.style.display = 'none';
    void fs.offsetHeight;
    fs.style.display = '';
  });

  for (const btn of [main, sticky]) {
    if (!btn) continue;
    setLabel(btn, SIZE_TEXT);
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('hh-needs-size');
  }
  if (express) {
    express.classList.add('hh-express-locked');
    express.setAttribute('inert', '');
  }
  const stickyVariant = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
  if (sticky && stickyVariant) {
    stickyVariant.dataset.hhOriginal = stickyVariant.textContent;
    stickyVariant.textContent = '';
  }
}

/** @param {HTMLElement} picker */
function releaseSelection(picker) {
  if (picker.dataset.hhNeedsSelection !== 'true') return;
  delete picker.dataset.hhNeedsSelection;
  chosen.add(pickerKey(picker));

  const { main, sticky, express } = buttonsFor(picker);
  for (const btn of [main, sticky]) {
    if (!btn) continue;
    restoreLabel(btn);
    btn.removeAttribute('aria-disabled');
    btn.classList.remove('hh-needs-size');
  }
  if (express) {
    express.classList.remove('hh-express-locked');
    express.removeAttribute('inert');
  }
  const hint = picker.querySelector('.hh-size-hint');
  if (hint) hint.textContent = '';
}

/** @param {HTMLElement} picker */
function promptForSize(picker) {
  const fieldset = picker.querySelector('fieldset');
  const hint = hintFor(picker);
  hint.textContent = SIZE_HINT;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  fieldset?.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  const first = picker.querySelector('fieldset input[data-option-available="true"]') || picker.querySelector('fieldset input');
  first?.focus({ preventScroll: true });
  fieldset?.classList.remove('hh-shake');
  if (!reduce) setTimeout(() => fieldset?.classList.add('hh-shake'), 0);
}

function pendingPickerForButton(button) {
  if (button.closest('sticky-add-to-cart')) {
    return document.querySelector('variant-picker[data-hh-needs-selection="true"][data-template-product-match="true"]');
  }
  const form = button.closest('product-form-component');
  if (!form) return null;
  const scope = form.closest('dialog') || form.closest('.shopify-section') || document;
  return scope.querySelector(
    `variant-picker[data-product-id="${form.dataset.productId}"][data-hh-needs-selection="true"]`
  );
}

function initSizeSelection() {
  if (!HH.requireSize) return;

  const scan = (root) => {
    const pickers = root.matches?.('variant-picker[data-hh-require-selection]')
      ? [root]
      : Array.from(root.querySelectorAll?.('variant-picker[data-hh-require-selection]') || []);
    // Yield once so Horizon's own components finish connecting first.
    // (setTimeout, not rAF: rAF never fires in background tabs.)
    if (pickers.length) setTimeout(() => pickers.forEach(requireSelection), 0);
  };

  scan(document);

  // Quick-add dialogs inject product markup after load.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof HTMLElement) scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener(
    'change',
    (event) => {
      const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
      if (picker) releaseSelection(/** @type {HTMLElement} */ (picker));
    },
    true
  );

  // Block add-to-cart / sticky / form submit until a size is picked.
  const guard = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const button =
      event.type === 'submit'
        ? target.querySelector('[ref="addToCartButton"].hh-needs-size')
        : target.closest('[ref="addToCartButton"].hh-needs-size');
    if (!button) return;
    const picker = pendingPickerForButton(button);
    if (!picker) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    promptForSize(picker);
  };
  // window-level capture runs before Horizon's document-level declarative (on:click) handlers.
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);
}

/* ------------------------------------------------------------------ */
/* 2. Cart add-ons                                                     */
/* ------------------------------------------------------------------ */

function announce(text) {
  let region = document.getElementById('hh-live-region');
  if (!region) {
    region = document.createElement('div');
    region.id = 'hh-live-region';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  setTimeout(() => (region.textContent = text), 50);
}

/** Publishes a custom analytics event through Shopify's Customer Events (no-op if unavailable). */
function track(name, detail = {}) {
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch (e) {
    /* analytics must never break the storefront */
  }
}

/**
 * Adds lines through Shopify's cart API and lets Horizon re-render the cart via its standard
 * CartLinesUpdateEvent. Shopify's response is the only source of truth for success.
 * @param {{id: number|string, quantity: number}[]} items
 * @param {string} source
 * @returns {Promise<{ok: boolean, status?: number, message?: string}>}
 */
async function addLines(items, source) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);

  let events = null;
  try {
    events = await import('@shopify/events');
  } catch (e) {
    events = null;
  }
  const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
  if (events && deferred) {
    document.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity })),
        ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
      }),
    });
    const data = await response.json();
    const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
    const failed = !response.ok || data.status;
    deferred?.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: failed
        ? { didError: true, items: cart.items, source }
        : { items: cart.items, source, itemCount: items.reduce((n, i) => n + i.quantity, 0), sections: data.sections, didError: false },
    });
    if (failed) {
      return {
        ok: false,
        status: response.status,
        message: response.status === 422 ? 'Seçtiğin ürünlerden biri için yeterli stok yok.' : 'Ürün eklenemedi. Lütfen tekrar dene.',
      };
    }
    if (!deferred) window.location.reload();
    return { ok: true };
  } catch (e) {
    deferred?.reject?.(e);
    return { ok: false, message: 'Bağlantı hatası. Lütfen tekrar dene.' };
  }
}

function offerName(el) {
  return el?.closest('[data-hh-offer]')?.getAttribute('data-hh-offer') || '';
}

function initCartAddons() {
  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof HTMLFormElement && event.target.matches('[data-hh-addon-form]') ? event.target : null;
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (!button || button.getAttribute('aria-busy') === 'true') return; // one click = one add
    const id = new FormData(form).get('id');
    if (!id) return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const error = form.querySelector('.hh-addon__error');
    if (error) error.textContent = '';

    const offer = offerName(form);
    if (offer) track('hh_offer_click', { offer, action: 'add' });
    const result = await addLines([{ id: String(id), quantity: 1 }], offer ? `hh-offer-${offer}` : 'hh-cart-addon');

    button.removeAttribute('aria-busy');
    button.disabled = false;
    if (!result.ok) {
      if (error) error.textContent = result.message || '';
      announce(result.message || '');
      return;
    }
    if (offer) track('hh_offer_add', { offer, quantity: 1 });
    announce('Sepetine eklendi');
    // Horizon's morph may reuse this form for the next suggestion; reset its state.
    form.reset();
  });
}

/* ------------------------------------------------------------------ */
/* 2c. Favourites (this browser only)                                  */
/* ------------------------------------------------------------------ */

const FAV_KEY = 'hh_favorites_v1';

function readFavs() {
  try {
    const list = JSON.parse(window.localStorage.getItem(FAV_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function writeFavs(list) {
  try {
    window.localStorage.setItem(FAV_KEY, JSON.stringify(list.slice(0, 50)));
    return true;
  } catch (e) {
    return false;
  }
}

function initFavorites() {
  if (!HH.favorites) return;
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('hh-favs'));

  const renderButtons = () => {
    const handles = new Set(readFavs().map((f) => f.handle));
    document.querySelectorAll('[data-hh-fav]').forEach((btn) => {
      const on = handles.has(btn.getAttribute('data-handle'));
      btn.setAttribute('aria-pressed', String(on));
      const label = btn.querySelector('[data-hh-fav-label]');
      if (label) label.textContent = on ? 'Favorilerinde' : 'Favorilere ekle';
    });
    const count = handles.size;
    document.querySelectorAll('[data-hh-favs-count]').forEach((el) => {
      el.textContent = count ? String(count) : '';
      el.hidden = !count;
    });
  };

  const renderList = () => {
    if (!dialog) return;
    const list = dialog.querySelector('[data-hh-favs-list]');
    const empty = dialog.querySelector('[data-hh-favs-empty]');
    const favs = readFavs();
    if (!list) return;
    list.replaceChildren(
      ...favs.map((f) => {
        const li = document.createElement('li');
        li.className = 'hh-favs__item';
        const a = document.createElement('a');
        a.href = f.url;
        a.className = 'hh-favs__link';
        if (f.image) {
          const img = document.createElement('img');
          img.src = f.image;
          img.alt = '';
          img.width = 64;
          img.height = 64;
          img.loading = 'lazy';
          a.append(img);
        }
        const span = document.createElement('span');
        span.textContent = f.title;
        a.append(span);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'hh-favs__remove';
        remove.textContent = 'Kaldır';
        remove.setAttribute('aria-label', `${f.title} favorilerden kaldır`);
        remove.addEventListener('click', () => {
          writeFavs(readFavs().filter((x) => x.handle !== f.handle));
          renderList();
          renderButtons();
          announce('Favorilerinden kaldırıldı.');
        });
        li.append(a, remove);
        return li;
      })
    );
    if (empty) empty.hidden = favs.length > 0;
  };

  // Header entry next to the cart icon (no header template edits needed)
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const actions = cartIcon?.parentElement;
  if (dialog && actions && !document.querySelector('[data-hh-favs-open]')) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hh-favs-open header-actions__action button-unstyled';
    btn.setAttribute('data-hh-favs-open', '');
    btn.setAttribute('aria-label', 'Favoriler');
    btn.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 8 3.6 4.5 7.1 4.5c2 0 3.5 1.1 4.9 2.9 1.4-1.8 2.9-2.9 4.9-2.9 3.5 0 5.7 3.5 4.4 6.8-1.8 4.6-9.3 9.2-9.3 9.2z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg><span class="hh-favs-open__count" data-hh-favs-count hidden></span>';
    actions.insertBefore(btn, cartIcon);
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const favBtn = target.closest('[data-hh-fav]');
    if (favBtn) {
      const handle = favBtn.getAttribute('data-handle');
      let favs = readFavs();
      const exists = favs.some((f) => f.handle === handle);
      if (exists) {
        favs = favs.filter((f) => f.handle !== handle);
      } else {
        favs.unshift({
          handle,
          title: favBtn.getAttribute('data-title'),
          url: favBtn.getAttribute('data-url'),
          image: favBtn.getAttribute('data-image'),
        });
      }
      if (!writeFavs(favs)) {
        announce('Favoriler bu tarayıcıda kaydedilemedi.');
        return;
      }
      renderButtons();
      announce(exists ? 'Favorilerinden kaldırıldı.' : 'Favorilerine eklendi.');
      track(exists ? 'hh_favorite_remove' : 'hh_favorite_add', { handle });
      return;
    }
    if (target.closest('[data-hh-favs-open]') && dialog) {
      renderList();
      dialog.showModal();
      return;
    }
    if (target.closest('[data-hh-favs-close]') || target === dialog) dialog?.close();
  });

  window.addEventListener('storage', (e) => {
    if (e.key === FAV_KEY) renderButtons();
  });
  renderButtons();
}

/* ------------------------------------------------------------------ */
/* 3. Conditional sign-up dialog                                       */
/* ------------------------------------------------------------------ */

const store = {
  get(area, key) {
    try {
      return window[area].getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(area, key, value) {
    try {
      window[area].setItem(key, value);
    } catch (e) {
      /* storage unavailable: popup just won't persist state */
    }
  },
};

function initSignup() {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('hh-signup'));
  if (!HH.signupPopup || !dialog || typeof dialog.showModal !== 'function') return;

  // Successful submit: show the thank-you state once.
  dialog.querySelectorAll('[data-hh-close]').forEach((el) => el.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  if (dialog.dataset.posted === 'true') {
    store.set('localStorage', 'hh_signup_mute_until', String(Date.now() + 365 * 864e5));
    dialog.showModal();
    return;
  }

  if (window.Theme?.template?.name?.startsWith('product')) {
    const views = Number(store.get('sessionStorage', 'hh_pv') || 0) + 1;
    store.set('sessionStorage', 'hh_pv', String(views));
  }

  const eligibleBase = () =>
    store.get('sessionStorage', 'hh_signup_shown') !== '1' &&
    Number(store.get('localStorage', 'hh_signup_mute_until') || 0) < Date.now() &&
    Number(store.get('sessionStorage', 'hh_pv') || 0) >= 2;

  if (!eligibleBase()) return;

  let active = Number(store.get('sessionStorage', 'hh_active_s') || 0);
  let lastInput = Date.now();
  ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((type) =>
    window.addEventListener(type, () => (lastInput = Date.now()), { passive: true })
  );

  const timer = setInterval(async () => {
    if (document.visibilityState !== 'visible' || Date.now() - lastInput > 30000) return;
    active += 1;
    store.set('sessionStorage', 'hh_active_s', String(active));
    if (active < 60) return;

    clearInterval(timer);
    if (!eligibleBase() || document.querySelector('dialog[open]')) return;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (cart.item_count > 0) return;
    } catch (e) {
      return;
    }
    store.set('sessionStorage', 'hh_signup_shown', '1');
    dialog.showModal();
  }, 1000);

  const mute = () => store.set('localStorage', 'hh_signup_mute_until', String(Date.now() + 7 * 864e5));
  dialog.addEventListener('close', mute);
}

/* ------------------------------------------------------------------ */
/* 2d. Size filter = sizes that are in stock                           */
/* ------------------------------------------------------------------ */

/**
 * Shopify's size filter alone matches products that merely *have* the size (even sold out).
 * Combined with "Stok durumu: Stokta" it checks stock per variant. So when a shopper picks a
 * Beden value we also tick the in-stock box (visible to the shopper, removable).
 */
function initHideStockPills() {
  const mark = () => {
    document.querySelectorAll('.facets-remove__pill:not([data-hh-stock-pill])').forEach((pill) => {
      const text = pill.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (/^(stokta|tükendi|in stock|out of stock)/.test(text)) pill.setAttribute('data-hh-stock-pill', '');
    });
  };
  mark();
  new MutationObserver(mark).observe(document.body, { childList: true, subtree: true });
}

function initSizeFilterStock() {
  // Horizon reads the filter form on click, so this must run in the capture phase on window
  // (before its handlers). A checkbox's `checked` is already toggled when click is dispatched.
  const tickStock = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const input =
      target instanceof HTMLInputElement ? target : target?.closest('label')?.querySelector('input[type="checkbox"]');
    if (!(input instanceof HTMLInputElement) || !input.name.startsWith('filter.v.option.')) return;
    // for label clicks the toggle happens later; treat an unchecked input as "about to be checked"
    const willBeChecked = target === input ? input.checked : !input.checked;
    if (!willBeChecked) return;
    const form = input.form || input.closest('form');
    const stock = form?.querySelector('input[name="filter.v.availability"][value="1"]');
    if (stock instanceof HTMLInputElement && !stock.checked && !stock.disabled) stock.checked = true;
  };
  window.addEventListener('click', tickStock, true);
  window.addEventListener('change', tickStock, true);
}

/* ------------------------------------------------------------------ */
/* 2e. Sticky bar: show the chosen size like the size buttons (Beden: M) */
/* ------------------------------------------------------------------ */
const SIZE_SHORT = { xsmall: 'XS', small: 'S', medium: 'M', large: 'L', xlarge: 'XL', xxlarge: 'XXL', xxxlarge: 'XXXL' };

function initStickySizeLabel() {
  const el = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
  if (!el) return;
  const apply = () => {
    const raw = el.textContent.trim();
    if (!raw || raw.startsWith('Beden:')) return;
    const short = SIZE_SHORT[raw.toLowerCase().replace(/[\s-]/g, '')];
    if (short) el.textContent = `Beden: ${short}`;
  };
  apply();
  new MutationObserver(apply).observe(el, { childList: true, characterData: true, subtree: true });
}

/* ------------------------------------------------------------------ */
/* 2f. Cart: change size (same product, in-stock variants only)        */
/* ------------------------------------------------------------------ */
function initSizeSwap() {
  document.addEventListener('change', async (event) => {
    const select = event.target instanceof HTMLSelectElement && event.target.matches('[data-hh-swap]') ? event.target : null;
    if (!select) return;
    const key = select.dataset.lineKey;
    const current = select.dataset.current;
    const quantity = Number(select.dataset.quantity || 1);
    const next = select.value;
    if (!key || !next || next === current) return;
    select.disabled = true;
    try {
      // Remove the old line, then add the new size through the shared add path, which re-renders the
      // cart (prices, discount, savings, free-shipping bar) from Shopify's own response.
      const removed = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!removed.ok) throw new Error('remove');
      const result = await addLines([{ id: next, quantity }], 'hh-size-swap');
      if (!result.ok) {
        await addLines([{ id: current, quantity }], 'hh-size-swap-restore');
        announce(result.message || 'Beden değiştirilemedi.');
      } else {
        announce('Beden güncellendi.');
      }
    } catch (e) {
      announce('Beden değiştirilemedi. Lütfen tekrar dene.');
      select.value = current;
    } finally {
      select.disabled = false;
    }
  });
}

/* ------------------------------------------------------------------ */
/* 2g. Product page: "Takımı tamamla" (two products, own sizes)         */
/* ------------------------------------------------------------------ */
function initCompleteSet() {
  const fmt = (cents) => {
    const tl = Math.round(cents) / 100;
    const [i, d] = tl.toFixed(2).split('.');
    return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${d === '00' ? '' : ',' + d} TL`;
  };
  // Total is shown upfront: chosen sizes, else each piece's first available size.
  const update = (root) => {
    const selects = [...root.querySelectorAll('[data-hh-set-select]')];
    const total = root.querySelector('[data-hh-set-total]');
    const ship = root.querySelector('[data-hh-set-ship]');
    const summary = root.querySelector('[data-hh-free]');
    const button = root.querySelector('[data-hh-set-add]');
    const picked = selects.map((s) => {
      const o = s.selectedOptions[0];
      return o && o.value ? o : [...s.options].find((x) => x.value && !x.disabled);
    });
    if (picked.some((o) => !o)) return;
    const live = document.documentElement.getAttribute('data-hh-sale') === 'live';
    const regular = picked.reduce((n, o) => n + Number(o.dataset.price || 0), 0);
    const pay = picked.reduce((n, o) => n + Number((live ? o.dataset.salePrice : o.dataset.price) || 0), 0);
    if (total) {
      total.innerHTML =
        live && pay < regular
          ? `Takım toplamı: <strong>${fmt(pay)}</strong> <s>${fmt(regular)}</s>`
          : `Takım toplamı: <strong>${fmt(pay)}</strong>`;
    }
    const freeAt = Number(summary?.dataset.hhFree || 0);
    const fee = Number(summary?.dataset.hhFee || 0);
    if (ship && freeAt > 0) {
      ship.textContent =
        pay >= freeAt
          ? 'Bu takımda kargo ücretsiz.'
          : `Kargo ${fmt(fee)} · ${fmt(freeAt)} ve üzeri ücretsiz kargo`;
    }
    if (button) button.disabled = selects.some((s) => !s.value);
  };
  document.addEventListener('change', (event) => {
    const root = event.target instanceof Element ? event.target.closest('[data-hh-set]') : null;
    if (root) update(root);
  });
  document.addEventListener('hh:sale-state', () => document.querySelectorAll('[data-hh-set]').forEach(update));
  document.querySelectorAll('[data-hh-set]').forEach(update);
  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-hh-set-add]') : null;
    if (!button) return;
    const root = button.closest('[data-hh-set]');
    const status = root?.querySelector('[data-hh-set-status]');
    const items = [...root.querySelectorAll('[data-hh-set-select]')].map((s) => ({ id: s.value, quantity: 1 }));
    if (items.some((i) => !i.id)) return;
    button.disabled = true;
    if (status) status.textContent = '';
    const result = await addLines(items, 'hh-complete-set');
    if (status) status.textContent = result.ok ? 'İki parça sepete eklendi.' : result.message || 'Eklenemedi.';
    button.disabled = false;
  });
}

/* Banner CTA: one custom event per click (Customer events → custom pixel can subscribe to
   "hh_banner_cta_click"). Shopify's own page_viewed / product events are not re-sent. */
function initBannerCtaEvent() {
  if (window.__hhBannerCta) return;
  window.__hhBannerCta = true;
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-hh-banner-cta]') : null;
    if (!link) return;
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    publish('hh_banner_cta_click', {
      sale_state: document.documentElement.getAttribute('data-hh-sale') || '',
      destination: link.getAttribute('href') || '',
    });
  });
}

/* "Hypehub sizde.": open a shared post large in a dialog. Native <dialog> gives Esc + focus
   handling; ← / → move between posts; focus returns to the post that opened it. */
function initSocialLightbox() {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest('[data-hh-social-open]');
    const root = (opener || target)?.closest('[data-hh-social]');
    if (!root) return;
    const dialog = root.querySelector('[data-hh-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const items = [...root.querySelectorAll('[data-hh-social-open]')];
    const img = dialog.querySelector('[data-hh-lightbox-img]');
    const cap = dialog.querySelector('[data-hh-lightbox-cap]');
    const show = (index) => {
      const n = (index + items.length) % items.length;
      const item = items[n];
      dialog.dataset.index = String(n);
      img.src = item.dataset.full;
      img.alt = item.dataset.alt || item.dataset.caption || 'Müşteri paylaşımı';
      cap.textContent = item.dataset.caption || '';
      dialog.querySelectorAll('[data-hh-lightbox-prev], [data-hh-lightbox-next]').forEach((b) => {
        b.hidden = items.length < 2;
      });
    };
    if (opener) {
      root._hhOpener = opener;
      show(items.indexOf(opener));
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('[data-hh-lightbox-close]')?.focus();
      if (!dialog._hhBound) {
        dialog._hhBound = true;
        dialog.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') show(Number(dialog.dataset.index) + 1);
          if (e.key === 'ArrowLeft') show(Number(dialog.dataset.index) - 1);
        });
        dialog.addEventListener('close', () => root._hhOpener?.focus());
        // click on the dimmed backdrop closes
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
      }
      return;
    }
    if (target.closest('[data-hh-lightbox-close]')) dialog.close();
    else if (target.closest('[data-hh-lightbox-next]')) show(Number(dialog.dataset.index) + 1);
    else if (target.closest('[data-hh-lightbox-prev]')) show(Number(dialog.dataset.index) - 1);
  });
}

initSizeSelection();
initBannerCtaEvent();
initSocialLightbox();
initSizeSwap();
initCompleteSet();
initStickySizeLabel();
initSizeFilterStock();
initHideStockPills();
initCartAddons();
initFavorites();
initSignup();

